import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { validateWorkspaceForCreate } from '@/lib/workspaceValidation';
import { useLeads, Lead } from '@/hooks/useLeads';
import { useAuth } from '@/lib/auth';
import { createDealNotifications } from '@/lib/dealNotifications';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { errorMessage } from '@/lib/errors';
import { Loader2, DollarSign, Zap, Plus } from 'lucide-react';

interface QuickAddDealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedLead?: Lead | null;
}

const STAGES = [
  { value: 'new', label: 'New', probability: 20 },
  { value: 'qualified', label: 'Qualified', probability: 40 },
  { value: 'proposal_sent', label: 'Proposal', probability: 60 },
  { value: 'negotiation', label: 'Negotiation', probability: 75 },
];

const CURRENCIES = [
  { value: 'EUR', symbol: '€' },
  { value: 'USD', symbol: '$' },
  { value: 'GBP', symbol: '£' },
  { value: 'BRL', symbol: 'R$' },
];

export function QuickAddDealModal({ open, onOpenChange, preselectedLead }: QuickAddDealModalProps) {
  const { activeWorkspaceId, mode } = useWorkspaceScope();
  const { data: leads } = useLeads();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    leadId: '',
    value: '',
    currency: 'EUR',
    stage: 'new',
    notes: '',
    expected_close_date: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update form when preselected lead changes
  useEffect(() => {
    if (preselectedLead) {
      setFormData(prev => ({
        ...prev,
        leadId: preselectedLead.id,
        title: `Deal with ${preselectedLead.name}`,
      }));
    }
  }, [preselectedLead]);

  // Reset form when modal opens without preselected lead
  useEffect(() => {
    if (open && !preselectedLead) {
      setFormData({
        title: '',
        leadId: '',
        value: '',
        currency: 'EUR',
        stage: 'new',
        notes: '',
        expected_close_date: '',
      });
      setErrors({});
    }
  }, [open, preselectedLead]);

  const openLeads = leads?.filter(l =>
    l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
  ) || [];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.stage) {
      newErrors.stage = 'Stage is required';
    }

    if (!formData.leadId && !preselectedLead) {
      newErrors.leadId = 'Please select a lead';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      // Validate workspace before creating deal
      const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'create a deal');
      if (!validation.isValid) {
        throw new Error(validation.errorMessage || 'No workspace available');
      }
      
      const workspaceId = validation.workspaceId!;

      const leadId = preselectedLead?.id || formData.leadId;
      
      if (!leadId) {
        throw new Error('Please select a lead');
      }

      if (!validate()) {
        throw new Error('Please fix the form errors');
      }

      const selectedLead = leads?.find(l => l.id === leadId);
      const stageInfo = STAGES.find(s => s.value === formData.stage);

      // Generate title if not provided
      const title = formData.title.trim() || 
        (selectedLead ? `Deal with ${selectedLead.name}` : `New Deal - ${new Date().toLocaleDateString()}`);

      // Use DealsService instead of direct supabase call
      const { DealsService } = await import('@/data/deals.service');
      return DealsService.createDeal({
        workspace_id: workspaceId,
        lead_id: leadId,
        title,
        value: formData.value ? parseFloat(formData.value) : null,
        currency: formData.currency,
        stage: formData.stage as any,
        probability: stageInfo?.probability || 20,
        expected_close_date: formData.expected_close_date || null,
      });
    },
    onSuccess: async (createdDeal) => {
      // Invalidate with correct workspaceId (must match useDeals query key)
      const workspaceId = activeWorkspaceId!;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['revenue-analytics', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] }), // Also refresh leads
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      ]);
      
      // Force refetch to ensure deals appear immediately
      await queryClient.refetchQueries({ queryKey: ['deals', workspaceId] });
      
      // Always create at least one immediate notification (deal_in_play).
      // If expected_close_date is set, helper will also schedule reminders.
      if (workspaceId && user?.id) {
        const leadId = preselectedLead?.id || formData.leadId;
        const selectedLead = leads?.find(l => l.id === leadId);
        const dealTitle = formData.title.trim() || 
          (selectedLead ? `Deal with ${selectedLead.name}` : `New Deal - ${new Date().toLocaleDateString()}`);
        try {
          await createDealNotifications({
            dealId: createdDeal.id,
            leadId: (leadId || null) as any,
            workspaceId,
            userId: user.id,
            expectedCloseDate: formData.expected_close_date || null,
            dealTitle,
            dealValue: formData.value ? parseFloat(formData.value) : null,
          });
        } catch (error) {
          // Non-blocking
          console.warn('[QuickAddDealModal] Failed to create notifications', error);
        }
      }
      
      toast({ 
        title: 'Deal created', 
        description: 'Deal added to your pipeline.' 
      });
      onOpenChange(false);
      // Reset form
      setFormData({
        title: '',
        leadId: '',
        value: '',
        currency: 'EUR',
        stage: 'new',
        notes: '',
        expected_close_date: '',
      });
      setErrors({});
    },
    onError: (error) => {
      console.error('[QuickAddDeal] Error:', error);
      toast({ 
        title: 'Error', 
        description: errorMessage(error, 'Failed to create deal'), 
        variant: 'destructive' 
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      leadId: '',
      value: '',
      currency: 'EUR',
      stage: 'new',
      notes: '',
      expected_close_date: '',
    });
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Add Deal
          </DialogTitle>
          <DialogDescription>
            Create a new deal in your pipeline.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Deal Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Deal Name</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Enterprise Software License"
            />
          </div>

          {/* Lead Selection */}
          {!preselectedLead && (
            <div className="space-y-2">
              <Label>Lead *</Label>
              <Select
                value={formData.leadId || '__none__'}
                onValueChange={(v) => {
                  const leadId = v === '__none__' ? '' : v;
                  const selectedLead = leads?.find(l => l.id === leadId);
                  setFormData({ 
                    ...formData, 
                    leadId,
                    title: formData.title || (selectedLead ? `Deal with ${selectedLead.name}` : ''),
                  });
                  if (leadId) {
                    setErrors(prev => ({ ...prev, leadId: '' }));
                  }
                }}
              >
                <SelectTrigger className={errors.leadId ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a lead" />
                </SelectTrigger>
                <SelectContent>
                  {openLeads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.name} {lead.company && `(${lead.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.leadId && (
                <p className="text-xs text-destructive">{errors.leadId}</p>
              )}
            </div>
          )}

          {preselectedLead && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="font-medium text-sm">{preselectedLead.name}</p>
              {preselectedLead.company && (
                <p className="text-xs text-muted-foreground">{preselectedLead.company}</p>
              )}
            </div>
          )}

          {/* Value + Currency */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="value">Value</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="value"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="10000"
                  className="pl-9"
                  min="0"
                  step="1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(v) => setFormData({ ...formData, currency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.symbol} {c.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stage */}
          <div className="space-y-2">
            <Label>Stage *</Label>
            <Select
              value={formData.stage}
              onValueChange={(v) => setFormData({ ...formData, stage: v })}
            >
              <SelectTrigger className={errors.stage ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.stage && (
              <p className="text-xs text-destructive">{errors.stage}</p>
            )}
          </div>

          {/* Expected Close Date */}
          <div className="space-y-2">
            <Label htmlFor="expected_close_date">Expected Close Date (optional)</Label>
            <Input
              id="expected_close_date"
              type="date"
              value={formData.expected_close_date}
              onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about this deal..."
              rows={2}
            />
          </div>

          <Button
            type="submit"
            variant="contrast"
            className="w-full gap-2"
            disabled={createMutation.isPending || (!preselectedLead && !formData.leadId)}
          >
            {createMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create Deal
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
